import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import ExtractReview from "@/pages/ExtractReview";
import InvoicePreview from "@/pages/InvoicePreview";
import History from "@/pages/History";
import Settings from "@/pages/Settings";
import ReverseInvoice from "@/pages/ReverseInvoice";
import Analytics from "@/pages/Analytics";
import ManualInvoice from "@/pages/ManualInvoice";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import Trash from "@/pages/Trash";
import Landing from "@/pages/Landing";

function ProtectedRoute({ children }) {
  const { user, booting } = useAuth();
  const location = useLocation();
  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

// Root gate: unauthenticated visitors to "/" see the public landing page;
// authenticated users land directly on the dashboard inside the app layout.
function RootGate() {
  const { user, booting } = useAuth();
  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Landing />;
  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Home: landing (unauthed) or dashboard (authed) */}
            <Route path="/" element={<RootGate />} />

            {/* All other authenticated routes */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/extract" element={<ExtractReview />} />
              <Route path="/reverse" element={<ReverseInvoice />} />
              <Route path="/manual" element={<ManualInvoice />} />
              <Route path="/invoice/:id" element={<InvoicePreview />} />
              <Route path="/history" element={<History />} />
              <Route path="/trash" element={<Trash />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/admin" element={<Admin />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
